import React, { useState, useEffect } from 'react';
import DynamicSection from '../components/dynamic/DynamicSection';
import DynamicCard from '../components/dynamic/DynamicCard';
import { testimonialsAPI } from '../services/api';
import { Testimonial } from '../types';
import { FaStar, FaQuoteLeft, FaUser } from 'react-icons/fa';

// Helper function to convert country code to flag emoji
const getCountryFlag = (countryCode: string | undefined): string => {
  if (!countryCode) return '';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const Testimonials: React.FC = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        const response = await testimonialsAPI.getAll();
        if (response && response.data) {
          setTestimonials(Array.isArray(response.data) ? response.data : []);
        } else {
          setTestimonials([]);
        }
      } catch (error: any) {
        console.error('Error loading testimonials:', error);
        setTestimonials([]);
      } finally {
        setLoading(false);
      }
    };
    loadTestimonials();
  }, []);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-xl text-gray-600 dark:text-gray-400">Loading testimonials...</div>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-white dark:bg-gray-900 min-h-screen">
      <DynamicSection
        title="Client Testimonials"
        subtitle="What clients say about working with me"
      >
        {testimonials.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">No testimonials found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((testimonial) => (
            <DynamicCard key={testimonial.id} className="p-6">
              <div className="mb-4">
                <FaQuoteLeft className="text-4xl text-primary-600 dark:text-primary-400 mb-4" />
                <p className="text-gray-700 dark:text-gray-300 italic mb-4">
                  "{testimonial.content}"
                </p>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                    {testimonial.imageUrl ? (
                      <img
                        src={testimonial.imageUrl}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <FaUser className="text-primary-600 dark:text-primary-400" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white">
                      {testimonial.name}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {testimonial.role}
                      {testimonial.company && ` • ${testimonial.company}`}
                      {testimonial.country && (
                        <span className="ml-2">
                          {testimonial.countryCode && getCountryFlag(testimonial.countryCode)}
                          {' '}
                          {testimonial.country}
                        </span>
                      )}
                    </p>
                    {testimonial.project && (
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Project: {testimonial.project}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <FaStar
                      key={i}
                      className={`w-5 h-5 ${
                        i < testimonial.rating
                          ? 'text-yellow-400'
                          : 'text-gray-300 dark:text-gray-600'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-3">
                {new Date(testimonial.date).toLocaleDateString()}
              </p>
            </DynamicCard>
            ))}
          </div>
        )}
      </DynamicSection>
    </div>
  );
};

export default Testimonials;
